<!-- Current conditions, and the warning strip above them.

     Pads its own top by env(safe-area-inset-top) rather than letting the page
     do it: paper is a full-bleed background, so without this the red warning
     strip bleeds up into the status bar and the notch looks like part of the
     alert. -->
<template>
  <header class="conditions">
    <button
      v-if="warning"
      class="warning"
      type="button"
      @click="$emit('open-warning', warning.id)"
    >
      <IonIcon :icon="warningOutline" aria-hidden="true" />
      <span>{{ warning.headline }} · until {{ clock(warning.expiresAt) }}</span>
    </button>

    <div class="place">
      <h1>{{ location.name }}</h1>
      <p class="meta">
        {{ location.postcode }}
        <template v-if="observation"> · observed {{ clock(observation.recordedAt) }}</template>
      </p>
    </div>

    <div v-if="observation" class="readings">
      <p class="temp">{{ Math.round(observation.tempC) }}°</p>
      <div class="detail">
        <p class="meta">
          feels {{ Math.round(observation.feelsLikeC) }}° ·
          {{ String(observation.windDirection || '').toLowerCase() }}
          {{ Math.round(observation.windKph) }} km/h
        </p>
        <p class="meta">{{ Number(observation.rainSince9amMm || 0).toFixed(1) }} mm since 9am</p>
      </div>
    </div>

    <p v-else class="readings-absent meta">No current observation for this place.</p>
  </header>
</template>

<script setup>
import { IonIcon } from '@ionic/vue';
import { warningOutline } from 'ionicons/icons';

defineProps({
  location: { type: Object, required: true },
  observation: { type: Object, default: null },
  warning: { type: Object, default: null },
});
defineEmits(['open-warning']);

const clock = (iso) => (iso
  ? new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false })
  : '');
</script>

<style scoped>
.conditions {
  --paper: var(--oh-paper, #dfe3d1);
  --ink: var(--oh-ink, #1b2540);
  --signal: var(--oh-signal, #a8331a);
  background: var(--paper);
  padding-top: env(safe-area-inset-top, 0px);
}
.warning {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  border: 0;
  border-radius: 0;
  background: var(--signal);
  color: var(--paper);
  font-family: 'Chivo Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-align: left;
  cursor: pointer;
}
.warning:focus-visible { outline: 2px solid var(--ink); outline-offset: -4px; }
.warning ion-icon { font-size: 16px; flex: none; }
/* A flex child will not shrink below its content by default, so a long bureau
   headline pushed the strip wider than the screen instead of wrapping inside
   it. min-width:0 is what lets it wrap; the headline is not ours to shorten. */
.warning span { min-width: 0; overflow-wrap: anywhere; }
.place { padding: 16px 14px 4px; }
h1 {
  margin: 0;
  font-family: 'Newsreader', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  font-size: 27px;
  line-height: 1.1;
  color: var(--ink);
}
.meta {
  margin: 3px 0 0;
  font-family: 'Chivo Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: color-mix(in srgb, var(--ink) 60%, transparent);
}
.readings { display: flex; align-items: flex-end; gap: 16px; padding: 10px 14px 12px; }
.temp {
  margin: 0;
  font-family: 'Chivo Mono', ui-monospace, SFMono-Regular, monospace;
  font-weight: 500;
  font-size: 38px;
  line-height: 1;
  color: var(--ink);
}
.detail { padding-bottom: 4px; }
.detail .meta + .meta { margin-top: 4px; }
.readings-absent { padding: 10px 14px 12px; }
</style>
